import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { BrowseTrack } from "@touchscreen/api-client";
import { useTouchscreenContext } from "@touchscreen/hooks";
import { useMyRequests } from "../lib/useMyRequests.js";
import { useLikedTracks } from "../lib/useLikedTracks.js";
import { getCachedTrack } from "../lib/trackCache.js";
import { dateGroupKey, dateGroupLabel } from "../lib/dateGroups.js";
import { artworkUrl } from "../lib/artwork.js";
import { TrackRow } from "../components/TrackRow.js";
import { LikeButton } from "../components/LikeButton.js";
import { RequestSheetHost } from "../components/RequestSheetHost.js";
import "./MyMusicScreen.css";

type Tab = "requests" | "liked";

/**
 * "What this punter has queued or hearted" — both lists are read straight out of localStorage,
 * not the server; they're transient, per-device data. Row metadata (title/artist) is resolved
 * from `trackCache.ts` by `smId` since neither list stores it directly any more.
 */
export function MyMusicScreen(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("requests");
  const requests = useMyRequests();
  const liked = useLikedTracks();
  const { resetVenue } = useTouchscreenContext();
  const [selectedTrack, setSelectedTrack] = useState<BrowseTrack | null>(null);

  const requestAgain = (smId: string) => {
    const track = getCachedTrack(smId);
    setSelectedTrack({
      sm_id: smId,
      title: track?.title ?? "Unknown track",
      artist: track?.artist ?? "",
      artwork_ref: null,
    });
  };

  const requestGroups = useMemo(() => {
    const groups: { key: string; label: string; entries: typeof requests }[] = [];
    for (const entry of requests) {
      const key = dateGroupKey(entry.requestedAtMs);
      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.key === key) {
        lastGroup.entries.push(entry);
      } else {
        groups.push({ key, label: dateGroupLabel(entry.requestedAtMs), entries: [entry] });
      }
    }
    return groups;
  }, [requests]);

  return (
    <div className="my-music-screen">
      <div className="my-music-screen__header">
        <h1 className="my-music-screen__title">My Music</h1>
        <button type="button" className="my-music-screen__change-venue" onClick={resetVenue}>
          Change venue
        </button>
      </div>

      <div className="my-music-screen__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "requests"}
          className={`my-music-screen__tab${tab === "requests" ? " my-music-screen__tab--active" : ""}`}
          onClick={() => setTab("requests")}
        >
          Requests
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "liked"}
          className={`my-music-screen__tab${tab === "liked" ? " my-music-screen__tab--active" : ""}`}
          onClick={() => setTab("liked")}
        >
          Liked Tracks
        </button>
      </div>

      {tab === "requests" ? (
        requestGroups.length === 0 ? (
          <EmptyState hint="You haven't queued anything yet." />
        ) : (
          <div className="my-music-screen__groups">
            {requestGroups.map((group) => (
              <section key={group.key} className="my-music-screen__group">
                <h2 className="my-music-screen__group-label">{group.label}</h2>
                <div className="my-music-screen__list">
                  {group.entries.map((entry) => {
                    const track = getCachedTrack(entry.smId);
                    return (
                      <TrackRow
                        key={entry.requestId}
                        title={track?.title ?? "Unknown track"}
                        artist={track?.artist ?? ""}
                        artworkUrl={artworkUrl(entry.smId)}
                        onClick={() => requestAgain(entry.smId)}
                        trailing={<LikeButton smId={entry.smId} title={track?.title ?? ""} artist={track?.artist ?? ""} />}
                      />
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )
      ) : liked.length === 0 ? (
        <EmptyState hint="You haven't liked anything yet." />
      ) : (
        <div className="my-music-screen__list">
          {liked.map((entry) => {
            const track = getCachedTrack(entry.smId);
            return (
              <TrackRow
                key={entry.smId}
                title={track?.title ?? "Unknown track"}
                artist={track?.artist ?? ""}
                artworkUrl={artworkUrl(entry.smId)}
                onClick={() => requestAgain(entry.smId)}
                trailing={<LikeButton smId={entry.smId} title={track?.title ?? ""} artist={track?.artist ?? ""} />}
              />
            );
          })}
        </div>
      )}

      {selectedTrack ? <RequestSheetHost track={selectedTrack} onClose={() => setSelectedTrack(null)} /> : null}
    </div>
  );
}

function EmptyState(props: { hint: string }): React.JSX.Element {
  return (
    <div className="my-music-screen__empty">
      <p className="my-music-screen__hint">{props.hint}</p>
      <Link to="/" className="my-music-screen__browse-link">
        Browse tracks
      </Link>
    </div>
  );
}
